/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import { QueryErrorResetBoundary } from 'react-query';
import { ErrorBoundary } from 'react-error-boundary';
import Snackbar from '@mui/material/Snackbar';
import Link from '@mui/material/Link';
import MuiAlert from '@mui/material/Alert';
import { sendErrorReport, AuthenticateError } from '../../api';
import Loader from '../Loader';
import { useAuth } from '../authentication';
import { CONTACT_SUPPORT_LINK, SUPPORT_EMAIL } from '../../constants';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const snackbarPosition = { vertical: 'bottom', horizontal: 'right' };

function CenteredLoader() {
  return <Loader style={{ marginTop: 'auto' }} />;
}

function AutoReload({ error, resetErrorBoundary, setError }) {
  React.useEffect(() => {
    setTimeout(() => {
      setError(error);
      resetErrorBoundary();
    }, 500);
  }, [setError, error, resetErrorBoundary]);
  return <CenteredLoader />;
}

function ExpireUser() {
  const { expireUser } = useAuth();
  React.useEffect(() => {
    expireUser();
  }, [expireUser]);
  return <CenteredLoader />;
}

export default function SnackbarError({ children }) {
  const [error, setError] = React.useState();
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setError();
  };
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          onError={sendErrorReport}
          fallbackRender={(props) => {
            const Component =
              props.error instanceof AuthenticateError
                ? ExpireUser
                : AutoReload;
            return <Component setError={setError} {...props} />;
          }}
        >
          {children}
          <Snackbar
            anchorOrigin={snackbarPosition}
            open={Boolean(error)}
            autoHideDuration={6000}
            onClose={handleClose}
          >
            <Alert
              onClose={handleClose}
              severity="error"
              sx={{ width: '100%' }}
            >
              An error occurred. The report is sent to the server. <br /> You
              can also email support{' '}
              <Link
                sx={{ color: 'white' }}
                href={CONTACT_SUPPORT_LINK}
                target="_blank"
              >
                {SUPPORT_EMAIL}
              </Link>
            </Alert>
          </Snackbar>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
